import React, { Component, Fragment } from 'react';
import Page from 'shared/Page';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { triggerTransition } from '@uirouter/redux';

import MemberInfoContainer from '@v/member/containers/MemberInfoContainer';
import { getCheckoutStepsSettings } from 'checkout/selectors/ui';
import { getOrderTotal } from "checkout/selectors/cart";
import AlertContainer from 'checkout/views/cart/containers/AlertContainer';
import CartSummaryContainer from './CartSummaryContainer';
import QuestionsContainer from 'checkout/views/questions/containers/QuestionsContainer';
import DeliveryContainer from 'checkout/views/delivery/containers/DeliveryContainer';
import InsuranceContainer from 'checkout/views/insurance/containers/InsuranceContainer';
import LoyaltyProgramContainer from 'checkout/views/loyalty-programs/containers/LoyaltyProgramContainer';
import { isMemberComplete } from 'checkout/selectors/member';
import { isQuestionComplete } from 'checkout/selectors/questions';
import { isDeliveryComplete } from 'checkout/selectors/delivery';
import { isInsuranceComplete } from 'checkout/selectors/insurance';
import { groupPopupProductUpsellsByUpsellId } from 'checkout/views/upsells/selectors/upsells';
import { DISPLAY_TYPE_GRID } from 'checkout/views/upsells/reducers/upsells';
import { goToStep, setCheckoutOverlay } from 'checkout/actions/ui';
import TermsOfServiceContainer from './TermsOfServiceContainer';
import PaymentContainer from 'checkout/views/payment/containers/PaymentContainer';
import AddressContainer from 'checkout/views/member/containers/AddressContainer';
import EmailOptOutContainer from 'checkout/views/checkout/containers/EmailOptOutContainer';
import DonationContainer from 'checkout/views/donations/containers/DonationContainer';
import UpsellsContainer from 'checkout/views/upsells/containers/UpsellsContainer';
import TimeoutPromptContainer from 'checkout/views/timeout-prompt/containers/TimeoutPromptContainer';
import StripeEmvContainer from 'checkout/views/stripe-emv/containers/StripeEmvContainer';
import KioskLayoutContainer from '../../kiosk/containers/KioskLayoutContainer';
import CouponEntryButton from '../../ticket-picker/containers/CouponEntryButton';
import VirtualKeyboard from 'checkout/components/VirtualKeyboard';
import StepActionContainer from './StepActionContainer';
import UpsellPopup from '../../upsells/containers/UpsellPopup';
import FocusTrap from '@s/FocusTrap';
import Translation from '@s/Translation';
import { getInitialFocusSelector } from '../../../helpers/helpers';
import NameOnTicketContainer from 'checkout/views/names/containers/NameOnTicketContainer';
import EmbedFooter from '../../../components/EmbedFooter';
import QuestionOptInContainer from 'checkout/views/checkout/containers/QuestionOptInContainer';
import InlineCoupon from '../components/InlineCoupon';
import WeatherInsuranceContainer from 'checkout/views/insurance/containers/WeatherInsuranceContainer';
import { TranslationContext } from 'checkout/config/Translations';
import { selectEventNameFromListingConfig } from '../../../selectors/ui';
import MoreTimeModal from 'checkout/views/checkout/components/MoreTimeModal';
import { BrandedHeader } from '@s/BrandedHeader';
import { BackButton } from '../components/BackButton';
import { isListingMonetizationAEnabled } from '@b/helpers/ABTesting';

/**
 * Extends the Page component, which holds the code for rendering out
 * the components
 */

class CheckoutSteps extends Page {
    keyboard = null;
    map = {
        memberInfo: MemberInfoContainer,
        nameOnTicket: NameOnTicketContainer,
        questions: QuestionsContainer,
        delivery: DeliveryContainer,
        loyaltyProgram: LoyaltyProgramContainer,
        insurance: InsuranceContainer,
        weather: WeatherInsuranceContainer,
        payment: PaymentContainer,
        address: AddressContainer,
        donation: DonationContainer,
        TimeoutPromptContainer: TimeoutPromptContainer,
        StripeEmvPayment: StripeEmvContainer,
        CouponEntryButton: CouponEntryButton,
        upsells: UpsellsContainer,
        coupon: InlineCoupon
    };

    completeChecks = {
        memberInfo: isMemberComplete,
        questions: isQuestionComplete,
        delivery: isDeliveryComplete,
        insurance: isInsuranceComplete,
    };

    componentDidMount() {
        if (!this.props.isKiosk) {
            setTimeout(() => {
                window.scrollTo(0, 0);
            }, "4");
        }
    }

    getHeaderTitle = () => {
        return (
            <div className="checkout__header__title">
                {isListingMonetizationAEnabled() && this.props.isEmbed && <BackButton />}
                <h1><Translation>{'checkout__complete_your_reservation'}</Translation></h1>
            </div>
        )
    }

    getCartSummary = () => {
        return (
            <div className="checkout__cart_summary">
                <div className="checkout-banner">
                    {this.props.headerImage && <div className="checkout-banner__image">
                        <img src={this.props.headerImage} alt={this.props.eventName} />
                    </div>}
                </div>
                <CartSummaryContainer/>
            </div>
        );
    }

    render() {

        // don't show checkout steps if we don't have a reservation or if the reservation is completed/pending payment completion
        if (!this.props.reservationLoaded || this.props.reservationPendingPaymentCompletion || this.props.reservationIsCompleted) {
            return null;
        }

        // don't show checkout steps if we're going to confirmation
        if (this.props.confirmation) {
            return null;
        }

        if (this.props.showUpsellPopup) {
            /**
            Apply a class other than checkout--upsell-popup
             * in order to make the popup modal wider.  This may depend on the upsells
             * that should be displayed which can be found in this.props.popupProductUpsellsByUpsellId.
             * Keep in mind that grid/list layout is configured at the upsell level.
             */
            let shouldWidenModal = false;
            Object.values(this.props.popupProductUpsellsByUpsellId).forEach(productUpsells => {
                if (productUpsells.length > 1 && productUpsells[0]?.upsell?.display_type === DISPLAY_TYPE_GRID) {
                    shouldWidenModal = true;
                }
            });
            return (
                <FocusTrap
                    fallback = {'.checkout-wrap'}
                    focusTrapOptions={{
                        initialFocus: getInitialFocusSelector('#checkout__popup'),
                    }}
                >
                    <div id="checkout-wrap" className="checkout-wrap" aria-modal="true" tabIndex="-1">
                        <div id="checkout__popup" className={`checkout checkout-popup__upsell ${shouldWidenModal ? 'upsell-popup--wide' : 'upsell-popup--narrow'}`}>
                            <AlertContainer />
                            <UpsellPopup />
                        </div>
                    </div>
            </FocusTrap>
            );
        }

        if (this.props.isKiosk) {
            return (
                <KioskLayoutContainer splitPane={true}>
                    {this.renderSteps()}
                    {this.getKeyboard()}
                </KioskLayoutContainer>
            );
        }

        return (
          <TranslationContext.Consumer>
            {({ t }) => (
              <FocusTrap
                  fallback = {'.checkout-wrap'}
                  focusTrapOptions={{
                      initialFocus: getInitialFocusSelector('.input__control, .input__radio__input'),
                      preventScroll: true,
                      tabbableOptions: {
                        getShadowRoot: true,
                      },
                  }}
              >
                  <dialog id="checkout-wrap" className="checkout-wrap" aria-modal="true" aria-label={`${t('checkout__checkout_for_event', { event_name: this.props.eventName })}`} tabIndex="-1">
                    {!this.props.isEmbed && (
                      <BrandedHeader>
                        <BrandedHeader.DefaultHeaderLeft
                          childrenBefore={isListingMonetizationAEnabled() && !this.props.isEmbed && <BackButton />}
                        />
                      </BrandedHeader>
                    )}
                    <div className="checkout__main">
                        <AlertContainer />
                        <MoreTimeModal />
                        <div className="checkout__body">
                            {this.getHeaderTitle()}
                            <div className="checkout__contents">
                                {this.getCartSummary()}
                                <div className="checkout__form">
                                    {this.getHeaderTitle()}
                                    {this.getCartSummary()}
                                    {this.renderSteps()}
                                </div>
                            </div>

                            <div className="event-inventory__footer-wrap">
                            <EmbedFooter />
                            </div>
                        </div>
                    </div>
                  </dialog>
              </FocusTrap>
            )}
          </TranslationContext.Consumer>
        )
    }

    getRegistrationTranslation = () => {
        return isListingMonetizationAEnabled() ? 'checkout__order_details' : 'checkout__registration';
    }

  renderSteps = () => {
        const stepTitleTranslationMap = {
            'Registration': this.getRegistrationTranslation(),
            'Payment': 'checkout__payment',
            'Address': 'checkout__address',
            'Additional Info': 'checkout__additional_info',
        }

        let steps = this.props.steps.map((step, i) => {
            //do not show completed or incomplete step cards on kiosk
            if (this.props.isKiosk && this.props.currentStep !== i) {
                return null;
            }

            let stepTitleHeader = step.title;
            if (stepTitleHeader) {
                if (!stepTitleTranslationMap[stepTitleHeader]) {
                    console.error('No translation defined for step title: ' + stepTitleHeader);
                } else {
                    stepTitleHeader = stepTitleTranslationMap[stepTitleHeader];
                }
            }

            return(
                <div className={`${this.getStepCSSClass(i)}`} key={i}>
                    <div className="step__header" onClick={ this.changeStep(i)}>
                        <span className="step__number">{i + 1}</span>
                        <div className="step__name" role='heading' aria-level='2'>
                            <Translation>{stepTitleHeader}</Translation>
                        </div>
                        { i < this.props.currentStep && <div className="step__action">
                            <button className="button--clear step__action-button">
                                <i className="icon-mode_edit" aria-hidden="true"></i>  Edit
                            </button>
                        </div>}
                    </div>
                    {i === this.props.currentStep && <div className='step__body'>
                        {(i === this.props.steps.length - 1 && !(this.props.paymentHandlesSubmission && this.props.orderTotal > 0)) && <Fragment>
                          <LoyaltyProgramContainer />
                          </Fragment>
                        }
                        {this.getComponents()}
                        {(i === this.props.steps.length - 1 && !(this.props.paymentHandlesSubmission && this.props.orderTotal > 0)) && <Fragment>
                                <InsuranceContainer />
                                <TermsOfServiceContainer />
                                <EmailOptOutContainer />
                                <QuestionOptInContainer />
                                <StepActionContainer />
                            </Fragment>
                        }
                        {(i !== this.props.steps.length - 1 && !this.props.isKiosk) && <StepActionContainer />}
                    </div>}
                </div>
            )
        });

        return steps;
    }

    getKeyboard = () => {
        if(this.props.isKiosk) {
            return <VirtualKeyboard />
        }

        return null;
    }

    changeStep = (i) => {
        return () => {
            this.props.goToStep(i);
        }
    }

    getStepCSSClass = (i) => {
        let name = 'step';

        if(i < this.props.currentStep) {
            name += ' step--complete';
        } else {
            name += ' step--incomplete';
        }

        if(i === this.props.currentStep) {
            name += ' step--current';
        }

        return name;
    }
}

const mapStateToProps = (state, props) => {
    return {
        headerImage: state.ui.uiConfig?.images?.checkoutHeader ?? '',
        headerImageAltText: state.ui.uiConfig?.images?.checkoutHeaderAltText ?? '',
        eventName: selectEventNameFromListingConfig(state),
        currentStep: state.ui.currentStep,
        steps: state.ui.listingConfig.checkoutSteps,
        settings: getCheckoutStepsSettings(state),
        orderTotal: getOrderTotal(state).amount || 0,
        terminalId: state.payment.terminalId,
        state: state,
        isKiosk: state.ui.uiConfig.layout === "kiosk",
        isEmbed: state.ui.isEmbed,
        paymentHandlesSubmission: state.ui.paymentHandlesSubmission || state.ui.uiConfig.layout === "kiosk",
        showUpsellPopup: !!state.upsells.showPopup,
        popupProductUpsellsByUpsellId: groupPopupProductUpsellsByUpsellId(state),
        reservationLoaded: !!state.reservation.token,
        reservationPendingPaymentCompletion: !!state.reservation.isPendingPaymentCompletion,
        reservationIsCompleted: !!state.reservation.isCompleted,
        confirmation: !!state.ui.confirmation,
    }
}

const mapDispatchToProps = dispatch => {
    return bindActionCreators({
        goToStep,
        setCheckoutOverlay,
        triggerTransition,
    }, dispatch);
}

CheckoutSteps.propTypes = {
}

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutSteps)
